import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetBalanceQuery,
  useGetEventDetailQuery,
  useGetMyIpQuery,
  useGetTvChannelQuery,
  useMyBetsQuery,
  usePlaceBetMutation,
} from "../../../store/service/clientBetService";
import { baseUrl } from "../../../store/constant";
import ClientBetSlip from "./ClientBetSlip";
import ClientLiveScore from "./ClientLiveScore";
import { calcRunnerPnl, getMatchBets, summarizeFancySession } from "./clientBetPnl";
import {
  eventFingerprint,
  isSuspendedPrice,
  parseEventMarkets,
} from "./clientMatchMarkets";
import { marketTypeFromLabel, parseTeams, normalizeBetLimits } from "./clientUtils";
import "./Client.scss";

const getAllMatchBets = (bets, matchId) => {
  const id = Number(matchId);
  // My Bets tab: only unsettled (open) bets
  return (bets || []).filter(
    (b) => Number(b.matchId) === id && String(b.status || "").toLowerCase() === "open"
  );
};

const betTypeLabel = (type, market) => {
  if (market === "fancy" || String(market).includes("fancy")) {
    return type === "back" ? "YES" : "NO";
  }
  return type === "back" ? "LAGAI" : "KHAI";
};

const marketLabelOf = (marketType, matchName) => {
  const m = String(marketType || "").toLowerCase();
  if (m === "fancy" || /fancy/i.test(String(matchName || ""))) return "Fancy / Session";
  if (m === "match_odds") return "Match Odds";
  if (m === "bookmaker") return "Bookmaker";
  return "Market";
};

const cleanMatchName = (name) =>
  String(name || "")
    .replace(/\s*\((Fancy|Bookmaker|Match Odds)\)\s*$/i, "")
    .trim();

const formatPts = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return Math.round(v).toLocaleString("en-IN");
};

const OddButton = memo(function OddButton({
  className,
  active,
  suspended,
  rate,
  size,
  onClick,
}) {
  if (suspended) {
    return <div className={`play-odd-cell play-odd-suspended ${className || ""}`}>—</div>;
  }
  const sizeVal = Number(size);
  const showSize = Number.isFinite(sizeVal) && sizeVal > 0;
  return (
    <button
      type="button"
      className={`play-odd-cell odd-stack ${className || ""} ${active ? "active" : ""}`}
      onClick={onClick}>
      <div className="odd-stack-inner">
        <div className="fancy-rate">{rate}</div>
        {showSize ? <div className="fancy-size">{sizeVal}</div> : null}
      </div>
    </button>
  );
});

const ClientMatch = () => {
  const { matchId } = useParams();
  const nav = useNavigate();

  // 3s poll — 1s caused constant re-render / header flicker.
  const { data: eventData, isLoading } = useGetEventDetailQuery(matchId, {
    pollingInterval: 3000,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: true,
  });
  const { data: balanceData } = useGetBalanceQuery(undefined, {
    pollingInterval: 5000,
  });
  const { data: myBetsData } = useMyBetsQuery(undefined, {
    pollingInterval: 3000,
  });
  const { data: chids } = useGetTvChannelQuery(matchId, {
    refetchOnMountOrArgChange: false,
  });
  const { data: userIp } = useGetMyIpQuery();
  const [placeBet, { isLoading: placing }] = usePlaceBetMutation();

  const [activeTab, setActiveTab] = useState("live");
  const [selection, setSelection] = useState(null);
  const [betType, setBetType] = useState(null);
  const [odds, setOdds] = useState(null); // payout rate
  const [runLine, setRunLine] = useState(null); // fancy runs only
  const [marketLabel, setMarketLabel] = useState("Bookmaker");
  const [slipKey, setSlipKey] = useState(null);
  const [showTv, setShowTv] = useState(false);
  const [tvUrl, setTvUrl] = useState(null);

  // Hold last good payload so a bad poll never blanks / blinks the page.
  const lastGoodRef = useRef(null);
  const lastFpRef = useRef("");
  const [stableEvent, setStableEvent] = useState(null);

  useEffect(() => {
    if (!eventData) return;
    const fp = eventFingerprint(eventData);
    if (fp && fp === lastFpRef.current) return;
    lastFpRef.current = fp;
    lastGoodRef.current = eventData;
    setStableEvent(eventData);
  }, [eventData]);

  const markets = useMemo(
    () => parseEventMarkets(stableEvent || lastGoodRef.current),
    [stableEvent]
  );

  const {
    bookmakers,
    fancyList,
    matchOdds,
    matchName,
    inPlay,
    bmMin,
    bmMax,
    fancyMin,
    fancyMax,
  } = markets;

  const balance = balanceData?.data?.balance ?? 0;
  const channelId = chids?.data?.channelId;

  const openMatchBets = useMemo(
    () => getMatchBets(myBetsData?.data?.betList, matchId),
    [myBetsData, matchId]
  );
  const allMatchBets = useMemo(
    () => getAllMatchBets(myBetsData?.data?.betList, matchId),
    [myBetsData, matchId]
  );
  const openCount = openMatchBets.length;
  const runnerNames = useMemo(() => bookmakers.map((r) => r.nation), [bookmakers]);
  const moRunnerNames = useMemo(
    () => (matchOdds?.runners || []).map((r) => r.name).filter(Boolean),
    [matchOdds]
  );
  const { team1 } = useMemo(() => parseTeams(matchName), [matchName]);

  const moLimits = useMemo(
    () => normalizeBetLimits(matchOdds?.minBet ?? bmMin, matchOdds?.maxBet ?? bmMax),
    [matchOdds, bmMin, bmMax]
  );

  const slipLimits = useMemo(() => {
    if (marketLabel === "Fancy") return normalizeBetLimits(fancyMin, fancyMax);
    if (marketLabel === "Match Odds") return moLimits;
    return normalizeBetLimits(bmMin, bmMax);
  }, [marketLabel, fancyMin, fancyMax, moLimits, bmMin, bmMax]);
  const slipMin = slipLimits.min;
  const slipMax = slipLimits.max;

  useEffect(() => {
    if (!showTv || !channelId) return;
    let cancelled = false;

    const loadTv = async () => {
      try {
        const root = String(baseUrl || "").replace(/\/$/, "");
        const response = await fetch(`${root}/feed/tv-stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ channel: channelId, ipv4: userIp?.ip ?? "" }),
        });
        if (cancelled) return;
        const result = await response.json();
        if (result.status === 1 && result.data) {
          setTvUrl(result.data);
          return;
        }

        // Direct stream host fallback
        const direct = await fetch("https://api2.dbm9.com/api/tv-stream", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ channel: channelId, ipv4: userIp?.ip ?? "" }),
        });
        if (cancelled) return;
        const directJson = await direct.json();
        if (directJson.status === 1) setTvUrl(directJson.data);
        else message.warning("Live TV is unavailable right now");
      } catch {
        if (!cancelled) message.warning("Live TV is unavailable right now");
      }
    };

    loadTv();
    return () => {
      cancelled = true;
    };
  }, [showTv, channelId, userIp]);

  // Clear slip when leaving this match
  useEffect(() => {
    setSelection(null);
    setBetType(null);
    setOdds(null);
    setMarketLabel("Bookmaker");
    setSlipKey(null);
    setActiveTab("live");
    lastFpRef.current = "";
    lastGoodRef.current = null;
    setStableEvent(null);
  }, [matchId]);

  const clearSlip = useCallback(() => {
    setSelection(null);
    setBetType(null);
    setOdds(null);
    setRunLine(null);
    setMarketLabel("Bookmaker");
    setSlipKey(null);
  }, []);

  const switchTab = useCallback(
    (id) => {
      setActiveTab(id);
      clearSlip();
    },
    [clearSlip]
  );

  /**
   * @param value - bookmaker/MO: rate | fancy: run line (b1/l1)
   * @param sizeOrRate - bookmaker: size | fancy: payout rate (bs1/ls1)
   */
  const pickOdds = useCallback((team, type, value, market, key, sizeOrRate) => {
    if (isSuspendedPrice(value)) {
      message.warning("This market is suspended");
      return;
    }
    setSelection(team);
    setBetType(type);
    setMarketLabel(market);
    setSlipKey(key);
    if (market === "Fancy") {
      const line = Number(value);
      const rate = Number(sizeOrRate);
      if (!Number.isFinite(line) || line <= 0) {
        message.warning("Invalid run line");
        return;
      }
      if (!Number.isFinite(rate) || rate <= 0) {
        message.warning("Invalid fancy rate");
        return;
      }
      setRunLine(line);
      setOdds(rate);
    } else {
      setRunLine(null);
      setOdds(Number(value));
    }
  }, []);

  const handlePlaceBet = useCallback(
    async (stake, slipOdds) => {
      try {
        const mt = marketTypeFromLabel(marketLabel);
        const rate = slipOdds ?? odds;
        const body = {
          matchId: Number(matchId),
          matchName: `${matchName} (${marketLabel})`,
          selection,
          betType,
          odds: rate,
          stake,
          marketType: mt,
        };
        if (mt === "fancy") {
          body.line = runLine;
        }
        const res = await placeBet(body).unwrap();

        const bal = res?.data?.balance;
        message.success(
          bal != null
            ? `Bet placed · Balance ${Math.floor(Number(bal)).toLocaleString("en-IN")}`
            : res?.message || "Bet placed successfully"
        );
        clearSlip();
      } catch (err) {
        message.error(err?.data?.message || "Could not place bet");
      }
    },
    [placeBet, matchId, matchName, marketLabel, selection, betType, odds, runLine, clearSlip]
  );

  const renderSlip = (key) =>
    slipKey === key && selection ? (
      <ClientBetSlip
        key={key}
        compact
        selection={selection}
        betType={betType}
        odds={odds}
        runLine={runLine}
        marketType={marketTypeFromLabel(marketLabel)}
        marketLabel={marketLabel}
        minBet={slipMin}
        maxBet={slipMax}
        balance={balance}
        placing={placing}
        onCancel={clearSlip}
        onPlace={handlePlaceBet}
      />
    ) : null;

  if (isLoading && !stableEvent && !lastGoodRef.current) {
    return (
      <div className="play-loading">
        <Spin size="large" tip="Loading match…" />
      </div>
    );
  }

  return (
    <div className="play-match-page">
      <div className="play-top-bar">
        <button type="button" className="play-back-link" onClick={() => nav("/play")}>
          ← Back
        </button>
        <div className="play-match-title">{matchName}</div>
        {inPlay ? <span className="play-live-pill">LIVE</span> : null}
        {channelId ? (
          <button type="button" className="play-tv-chip" onClick={() => setShowTv((v) => !v)}>
            {showTv ? "Hide" : "TV"}
          </button>
        ) : null}
      </div>

      {showTv && tvUrl ? (
        <div className="play-tv-frame">
          <iframe title="Live TV" src={tvUrl} allowFullScreen />
        </div>
      ) : null}

      <div className="play-match-tabs play-match-tabs--2">
        <button
          type="button"
          className={activeTab === "live" ? "active" : ""}
          onClick={() => switchTab("live")}>
          Live
        </button>
        <button
          type="button"
          className={activeTab === "bets" ? "active" : ""}
          onClick={() => switchTab("bets")}>
          My Bets
          {allMatchBets.length ? <i>{allMatchBets.length}</i> : null}
        </button>
      </div>

      {openCount > 0 && activeTab !== "bets" ? (
        <button type="button" className="play-open-chip" onClick={() => switchTab("bets")}>
          {openCount} open bet{openCount > 1 ? "s" : ""} · View
        </button>
      ) : null}

      {activeTab === "live" ? (
        <>
          <ClientLiveScore matchId={matchId} />

          <div className="play-section-head w777-mkt-head">
            <span>Normal</span>
            <small>
              Min:{bmMin} Max:{bmMax}
            </small>
          </div>
          <div className="play-odds-table">
            <div className="play-odds-head">
              <div className="play-odds-team-col" />
              <div className="play-lagai-head">Lagai</div>
              <div className="play-khai-head">Khai</div>
            </div>

            {bookmakers.length > 0 ? (
              bookmakers.map((runner) => {
                const pnl = calcRunnerPnl(openMatchBets, runner.nation, runnerNames, "bookmaker");
                const backKey = `bm-${runner._key}-back`;
                const layKey = `bm-${runner._key}-lay`;
                const rowActive = slipKey === backKey || slipKey === layKey;
                const backSus =
                  isSuspendedPrice(runner.b1, runner.gstatus) ||
                  String(runner.gstatus || "").toUpperCase() === "SUSPENDED";
                const laySus =
                  isSuspendedPrice(runner.l1, runner.gstatus) ||
                  String(runner.gstatus || "").toUpperCase() === "SUSPENDED";

                return (
                  <div key={runner._key} className="play-odds-block">
                    <div className={`play-odds-row ${rowActive ? "selected" : ""}`}>
                      <div className="play-team-cell">
                        <div className="play-team-name">{runner.nation?.toUpperCase()}</div>
                        {pnl !== 0 ? (
                          <div className={`play-team-pnl ${pnl > 0 ? "pos" : "neg"}`}>
                            {pnl > 0 ? "+" : ""}
                            {pnl.toFixed(2)}
                          </div>
                        ) : null}
                      </div>
                      <OddButton
                        className="lagai"
                        active={slipKey === backKey}
                        suspended={backSus}
                        rate={runner.b1}
                        size={runner.bs1}
                        onClick={() =>
                          pickOdds(runner.nation, "back", runner.b1, "Bookmaker", backKey)
                        }
                      />
                      <OddButton
                        className="khai"
                        active={slipKey === layKey}
                        suspended={laySus}
                        rate={runner.l1}
                        size={runner.ls1}
                        onClick={() =>
                          pickOdds(runner.nation, "lay", runner.l1, "Bookmaker", layKey)
                        }
                      />
                    </div>
                    {renderSlip(backKey)}
                    {renderSlip(layKey)}
                  </div>
                );
              })
            ) : (
              <div className="play-fallback-row">
                <div className="play-team-cell">
                  <div className="play-team-name">{team1?.toUpperCase() || "—"}</div>
                </div>
                <div className="play-odd-cell play-odd-suspended">—</div>
                <div className="play-odd-cell play-odd-suspended">—</div>
              </div>
            )}
          </div>

          {matchOdds?.runners?.length > 0 ? (
            <>
              <div className="play-section-head w777-mkt-head yellow">
                <span>Match Odds</span>
                <small>
                  Min:{moLimits.min} Max:{moLimits.max}
                </small>
              </div>
              <div className="play-mo-list">
                {matchOdds.runners.map((r) => {
                  const back = r?.ex?.availableToBack?.[0]?.price;
                  const lay = r?.ex?.availableToLay?.[0]?.price;
                  const backKey = `mo-${r.selectionId}-back`;
                  const layKey = `mo-${r.selectionId}-lay`;
                  const pnl = calcRunnerPnl(
                    openMatchBets,
                    r.name,
                    moRunnerNames.length ? moRunnerNames : runnerNames,
                    "match_odds"
                  );
                  return (
                    <div key={r.selectionId} className="play-mo-block">
                      <div className="play-mo-row">
                        <div className="play-mo-name">
                          <strong>{r.name}</strong>
                          {pnl !== 0 ? (
                            <span className={`play-team-pnl ${pnl > 0 ? "pos" : "neg"}`}>
                              {pnl > 0 ? "+" : ""}
                              {pnl.toFixed(2)}
                            </span>
                          ) : null}
                        </div>
                        <OddButton
                          className="lagai tiny"
                          active={slipKey === backKey}
                          suspended={isSuspendedPrice(back)}
                          rate={back}
                          onClick={() => pickOdds(r.name, "back", back, "Match Odds", backKey)}
                        />
                        <OddButton
                          className="khai tiny"
                          active={slipKey === layKey}
                          suspended={isSuspendedPrice(lay)}
                          rate={lay}
                          onClick={() => pickOdds(r.name, "lay", lay, "Match Odds", layKey)}
                        />
                      </div>
                      {renderSlip(backKey)}
                      {renderSlip(layKey)}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}

          <div className="play-section-head w777-mkt-head fancy">
            <span>Fancy</span>
            <small>
              {fancyList.length} · Min:{fancyMin} Max:{fancyMax}
            </small>
          </div>
          <div className="play-fancy-table">
            <div className="play-fancy-head">
              <div className="play-fancy-name-col">Session</div>
              <div className="play-khai-head">No</div>
              <div className="play-lagai-head">Yes</div>
            </div>
            {fancyList.length === 0 ? (
              <div className="play-suspended" style={{ padding: 12 }}>
                No fancy sessions for this match right now
              </div>
            ) : (
              fancyList.map((f) => {
                const suspended = String(f.gstatus || "").toUpperCase() === "SUSPENDED";
                const noKey = `f-${f._key}-lay`;
                const yesKey = `f-${f._key}-back`;
                const pos = summarizeFancySession(openMatchBets, f.nation);
                const hasYes = pos.yesStake > 0;
                const hasNo = pos.noStake > 0;
                return (
                  <div
                    key={f._key}
                    className={`play-fancy-block${pos.hasBet ? " has-bet" : ""}`}>
                    <div className="play-fancy-row">
                      <div className="play-fancy-name">
                        <span className="play-fancy-title">{f.nation}</span>
                        {pos.hasBet ? (
                          <div className="play-fancy-pos">
                            {hasYes ? (
                              <em className="is-yes">YES {formatPts(pos.yesStake)}</em>
                            ) : null}
                            {hasNo ? (
                              <em className="is-no">NO {formatPts(pos.noStake)}</em>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      {suspended ? (
                        <div className="play-suspended" style={{ gridColumn: "2 / -1" }}>
                          SUSPENDED
                        </div>
                      ) : (
                        <>
                          <OddButton
                            className={`khai small fancy-odd${hasNo ? " has-my-bet" : ""}`}
                            active={slipKey === noKey || hasNo}
                            suspended={isSuspendedPrice(f.l1)}
                            rate={f.l1}
                            size={f.ls1}
                            onClick={() =>
                              pickOdds(f.nation, "lay", f.l1, "Fancy", noKey, f.ls1)
                            }
                          />
                          <OddButton
                            className={`lagai small fancy-odd${hasYes ? " has-my-bet" : ""}`}
                            active={slipKey === yesKey || hasYes}
                            suspended={isSuspendedPrice(f.b1)}
                            rate={f.b1}
                            size={f.bs1}
                            onClick={() =>
                              pickOdds(f.nation, "back", f.b1, "Fancy", yesKey, f.bs1)
                            }
                          />
                        </>
                      )}
                    </div>
                    {renderSlip(noKey)}
                    {renderSlip(yesKey)}
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : null}

      {activeTab === "bets" ? (
        <div className="play-bets-tab">
          {allMatchBets.length === 0 ? (
            <div className="lotus-empty">No open bets on this match</div>
          ) : (
            <>
              <div className="play-bets-summary">
                <span>
                  Open bets <strong>{allMatchBets.length}</strong>
                </span>
              </div>
              <div className="play-my-bets-list">
                {allMatchBets.map((bet) => {
                  const side = betTypeLabel(bet.betType, bet.marketType);
                  const market = marketLabelOf(bet.marketType, bet.matchName);
                  const isFancy = String(bet.marketType || "").toLowerCase() === "fancy";
                  return (
                    <div key={bet.betId} className={`play-my-bet-item st-${bet.status}`}>
                      <div className="play-my-bet-top">
                        <span className={`play-my-bet-mkt is-${String(bet.marketType || "bm").replace("_", "-")}`}>
                          {market}
                        </span>
                        <em className="play-my-bet-status">{bet.status}</em>
                      </div>
                      <div className="play-my-bet-event">{cleanMatchName(bet.matchName)}</div>
                      <div className="play-my-bet-selection">
                        {isFancy ? "Session: " : "Team: "}
                        <strong>{bet.selection || "—"}</strong>
                      </div>
                      <div className="play-my-bet-row">
                        <span className={`play-my-bet-side-chip is-${bet.betType}`}>
                          {side}
                        </span>
                        {isFancy && bet.line != null ? (
                          <span>
                            Runs <b>{bet.line}</b>
                          </span>
                        ) : null}
                        <span>
                          Rate <b>{bet.odds}</b>
                        </span>
                        <span>
                          Amount <b>{formatPts(bet.stake)}</b>
                        </span>
                      </div>
                      <div className="play-my-bet-row play-my-bet-row--muted">
                        {bet.potentialWin != null ? (
                          <span className="play-my-bet-pnl">
                            {bet.status === "won"
                              ? `Won +${formatPts(bet.payout ?? bet.potentialWin)}`
                              : bet.status === "lost"
                                ? "Lost"
                                : `Profit @ rate ${bet.odds}: +${formatPts(bet.potentialWin)}`}
                          </span>
                        ) : (
                          <span />
                        )}
                        {bet.placedOn ? <span>{bet.placedOn}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ClientMatch;
